import React from 'react';
import PropTypes from 'prop-types';
import './Pagination.css';

/**
 * Pagination Component
 * 
 * A navigation component for paginated data.
 * 
 * @param {Object} props - Component props
 * @param {number} props.currentPage - Current page number (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {function} props.onPageChange - Callback when page changes
 * @param {number} props.siblingCount - Number of page buttons on each side of current
 * @param {boolean} props.showFirstLast - Show first/last page buttons
 * @param {string} props.size - Pagination size: 'sm' | 'md' | 'lg'
 * @param {string} props.variant - Pagination style: 'default' | 'outlined'
 * @param {string} props.className - Additional CSS classes
 */
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  siblingCount = 1,
  showFirstLast = true,
  size = 'md',
  variant = 'default',
  className = '',
  ...props
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange?.(page);
    }
  };

  const handleKeyDown = (event, page) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePageChange(page);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > siblingCount * 2 + 5;

    if (!showEllipsis) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      // Calculate range around current page
      const leftSibling = Math.max(2, currentPage - siblingCount);
      const rightSibling = Math.min(totalPages - 1, currentPage + siblingCount);

      // Add left ellipsis
      if (leftSibling > 2) {
        pages.push('ellipsis-left');
      }

      // Add pages around current
      for (let i = leftSibling; i <= rightSibling; i++) {
        pages.push(i);
      }

      // Add right ellipsis
      if (rightSibling < totalPages - 1) {
        pages.push('ellipsis-right');
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const containerClasses = [
    'pagination',
    `pagination--${variant}`,
    `pagination--${size}`,
    className
  ].filter(Boolean).join(' ');

  const pageNumbers = getPageNumbers();

  return (
    <nav className={containerClasses} aria-label="Pagination" {...props}>
      <ul className="pagination__list">
        {/* First Page */}
        {showFirstLast && (
          <li className="pagination__item">
            <button
              type="button"
              className="pagination__button pagination__button--first"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              aria-label="First page"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M13 5L8 10L13 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 5L7 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </li>
        )}

        {/* Previous Page */}
        <li className="pagination__item">
          <button
            type="button"
            className="pagination__button pagination__button--prev"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 5L7 10L12 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </li>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => {
          if (typeof page === 'string' && page.startsWith('ellipsis')) {
            return (
              <li key={page} className="pagination__item pagination__item--ellipsis">
                <span className="pagination__ellipsis">...</span>
              </li>
            );
          }

          const isActive = page === currentPage;
          const buttonClasses = [
            'pagination__button',
            'pagination__button--page',
            isActive ? 'pagination__button--active' : ''
          ].filter(Boolean).join(' ');

          return (
            <li key={page} className="pagination__item">
              <button
                type="button"
                className={buttonClasses}
                onClick={() => handlePageChange(page)}
                onKeyDown={(e) => handleKeyDown(e, page)}
                aria-label={`Page ${page}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}

        {/* Next Page */}
        <li className="pagination__item">
          <button
            type="button"
            className="pagination__button pagination__button--next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </li>

        {/* Last Page */}
        {showFirstLast && (
          <li className="pagination__item">
            <button
              type="button"
              className="pagination__button pagination__button--last"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Last page"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7 5L12 10L7 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 5L13 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </li>
        )}
      </ul>

      {/* Page Info */}
      <div className="pagination__info">
        Page {currentPage} of {totalPages}
      </div>
    </nav>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  siblingCount: PropTypes.number,
  showFirstLast: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['default', 'outlined']),
  className: PropTypes.string,
};

export default Pagination;
